
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import * as Sentry from "@sentry/react";
import { BusinessData, BUSINESS_INDUSTRIES } from '@/api/business/types';
import { businessMapper } from '@/api/business/mappers';

export const useBusinessDataSaver = (
  businessData: BusinessData,
  setIsSaving: (value: boolean) => void,
  setIsDirty: (value: boolean) => void
) => {
  const supabaseClient = supabase;
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Save all changes
  const handleSave = async () => {
    if (!businessData.id) {
      toast({
        title: 'Cannot save changes',
        description: 'No business profile found. Please complete onboarding first.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Check authentication again before saving
      const { data: sessionData, error: sessionError } = await supabaseClient.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        console.error('Authentication error before saving:', sessionError);
        toast({
          title: 'Authentication error',
          description: 'Your session has expired. Please sign in again.',
          variant: 'destructive',
        });
        navigate('/login');
        return;
      }
      
      // Map frontend data to database fields using our mapper
      const dbFields = businessMapper.toDb(businessData);
      
      // Extra validation for industry_enum
      if (dbFields.industry_enum && 
          !BUSINESS_INDUSTRIES.includes(dbFields.industry_enum as any)) {
        dbFields.industry_enum = null;
      }
      
      console.log('Saving business data:', dbFields);
      
      const { error } = await supabaseClient
        .from('business')
        .update(dbFields)
        .eq('id', businessData.id);
        
      if (error) throw error;
      
      toast({
        title: 'Changes saved',
        description: 'Your business settings have been updated.',
        variant: 'default',
      });
      
      setIsDirty(false);
    } catch (error) {
      console.error('Error saving business data:', error);
      Sentry.captureException(error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast({
        title: 'Failed to save changes',
        description: `Error: ${errorMessage}. Please try again.`,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  return {
    handleSave
  };
};
